import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Badge, Button, Modal, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt, faCheckCircle, faTimesCircle,
  faUserCheck, faClock, faBook, faChartLine,
  faFilter, faStar, faClipboardCheck, faUserGraduate,
  faExclamationTriangle, faListAlt, faFileExcel, faFilePdf, faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Layout from '../../../../app/Layout';
import HeaderContent from '../../General/HeaderContent';
import Breadcrumb from '../../General/Breadcrumb';
import SpinnerLoading from '../../../components/Spinners/SpinnerLoading';
import authAxios from '../../../../config/authAxios';
import { avisoError } from '../../../components/AvisosMensajes/MensajesSwalf';
import { obtenerKpisAcademicos, obtenerProductosKpi, obtenerDocentesKpi } from '../../../../actions/kpisAcademicosActions';

/* ─── Helpers ──────────────────────────────────────────────────── */
const colorSemaforo = (pct, sinDatos = false) => {
  if (sinDatos || pct === null) return '#6c757d';
  if (pct >= 90) return '#28a745';
  if (pct >= 70) return '#fd7e14';
  return '#dc3545';
};

const labelSemaforo = (pct, sinDatos = false) => {
  if (sinDatos || pct === null) return 'Sin datos';
  if (pct >= 90) return 'Cumple';
  if (pct >= 70) return 'En riesgo';
  return 'No cumple';
};

const colorEstado = { verde: '#28a745', amarillo: '#fd7e14', rojo: '#dc3545' };

/* ─── Tarjeta KPI ──────────────────────────────────────────────── */
const KpiCard = ({ titulo, valor, unidad = '%', meta = 90, icon, sinDatos = false }) => {
  const num   = parseFloat(valor) || 0;
  const color = colorSemaforo(sinDatos ? null : num, sinDatos);
  const barW  = sinDatos ? 0 : Math.min(100, num);
  return (
    <div className="col-xl-3 col-md-4 col-sm-6 mb-3">
      <div className="box p-3 h-100" style={{ borderTop: `4px solid ${color}` }}>
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div style={{ width: 38, height: 38, borderRadius: 8, backgroundColor: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FontAwesomeIcon icon={icon} style={{ color, fontSize: 17 }} />
          </div>
          {!sinDatos && <FontAwesomeIcon icon={num >= meta ? faCheckCircle : faTimesCircle} style={{ color: num >= meta ? '#28a745' : '#dc3545', fontSize: 17 }} />}
        </div>
        <h2 style={{ color, fontWeight: 700, fontSize: 30, marginBottom: 2 }}>{sinDatos ? '—' : `${num}${unidad}`}</h2>
        <p className="mb-2" style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, color: '#444' }}>{titulo}</p>
        <div style={{ height: 4, backgroundColor: '#eee', borderRadius: 2 }}>
          <div style={{ height: 4, width: `${barW}%`, backgroundColor: color, borderRadius: 2, transition: 'width .4s' }} />
        </div>
        {!sinDatos && <small className="text-muted" style={{ fontSize: 12 }}>Meta: {meta}%</small>}
      </div>
    </div>
  );
};

/* ─── Fila KPI en bloque ───────────────────────────────────────── */
const KpiFila = ({ label, cumplidos, total, obligatorio = false }) => {
  const pct   = total > 0 ? Math.round((cumplidos / total) * 100) : null;
  const color = colorSemaforo(pct, pct === null);
  return (
    <div className="d-flex align-items-center justify-content-between py-2" style={{ borderBottom: '1px solid #f0f0f0', fontSize: 14 }}>
      <div className="d-flex align-items-center" style={{ flex: 1 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color, marginRight: 10, flexShrink: 0 }} />
        <span style={{ fontWeight: 500 }}>{label}</span>
        {obligatorio && <Badge variant="warning" className="ml-2" style={{ fontSize: 12 }}>Obligatorio</Badge>}
      </div>
      <div className="text-center" style={{ minWidth: 80 }}>
        <span style={{ fontWeight: 600 }}>{pct !== null ? `${pct}%` : '—'}</span>
        <small className="d-block text-muted" style={{ fontSize: 12 }}>{total > 0 ? `${cumplidos}/${total}` : 'Sin datos'}</small>
      </div>
      <div style={{ minWidth: 90, textAlign: 'right' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color, backgroundColor: `${color}15`, padding: '2px 8px', borderRadius: 20 }}>
          {labelSemaforo(pct, pct === null)}
        </span>
      </div>
    </div>
  );
};

/* ─── Bloque contenedor ────────────────────────────────────────── */
const BloqueKpi = ({ titulo, icon, pct, sinDatos, onVerMas, onInfo, children }) => {
  const color = colorSemaforo(pct, sinDatos);
  return (
    <div className="box mb-3">
      <div className="box-header d-flex align-items-center justify-content-between" style={{ borderBottom: `3px solid ${color}`, padding: '14px 18px' }}>
        <div className="d-flex align-items-center">
          <FontAwesomeIcon icon={icon} style={{ color, fontSize: 18, marginRight: 10 }} />
          <span style={{ fontWeight: 700, fontSize: 15 }}>{titulo}</span>
        </div>
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          {!sinDatos && <span style={{ fontSize: 22, fontWeight: 700, color, marginRight: 4 }}>{pct}%</span>}
          <span style={{ fontSize: 13, fontWeight: 600, color, backgroundColor: `${color}15`, padding: '3px 10px', borderRadius: 20 }}>
            {labelSemaforo(pct, sinDatos)}
          </span>
          {onInfo && (
            <Button variant="outline-info" size="sm" style={{ fontSize: 12, padding: '2px 8px' }} onClick={onInfo} title="Ver definición del KPI">
              <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />Definición
            </Button>
          )}
          {onVerMas && (
            <Button variant="outline-secondary" size="sm" style={{ fontSize: 13, padding: '2px 10px' }} onClick={onVerMas}>
              <FontAwesomeIcon icon={faListAlt} className="mr-1" />Ver más
            </Button>
          )}
        </div>
      </div>
      <div className="box-body" style={{ padding: '8px 18px 14px' }}>{children}</div>
    </div>
  );
};

/* ─── Badge estado recurso ─────────────────────────────────────── */
const BadgeRecurso = ({ estado }) => {
  if (!estado || estado === 'Sin registro') return <span className="text-muted" style={{ fontSize: 13 }}>—</span>;
  const map = { 'Completo': '#28a745', 'Pendiente': '#dc3545', 'En proceso': '#fd7e14' };
  const color = map[estado] || '#6c757d';
  return (
    <span style={{ fontSize: 12, fontWeight: 600, color, backgroundColor: `${color}20`, padding: '1px 6px', borderRadius: 10 }}>
      {estado}
    </span>
  );
};

/* ═══════════════════════════════════════════════════════════════ */
const ReporteKpis = ({ loading, datos, productos, docentes, obtenerKpisAcademicos, obtenerProductosKpi, obtenerDocentesKpi }) => {
  const now = new Date();
  const [anio,        setAnio]       = useState(now.getFullYear().toString());
  const [mes,         setMes]        = useState((now.getMonth() + 1).toString().padStart(2, '0'));
  const [idProducto,  setIdProducto] = useState('');
  const [idDocente,   setIdDocente]  = useState('');
  const [filtroKpi,   setFiltroKpi]  = useState('');
  const [filtroRec,   setFiltroRec]  = useState('');
  const [infoKpi,     setInfoKpi]    = useState(null); // 'implementacion' | 'organizacion' | 'calidad'

  const definicionesKpi = {
    implementacion: {
      titulo: 'Implementación Académica',
      contenido: (
        <div className="kpi-description">
          <div className="mb-3">
            <p>Este KPI <strong>mide la eficiencia en la contratación y sensibilización de docentes.</strong> El área de curaduría debe garantizar que, para cada sesión programada, <strong>los docentes sean confirmados y contratados al menos 7 días antes de su dictado.</strong> Además, es fundamental que <strong>los docentes sean sensibilizados en la metodología ANDRO® con un mínimo de 5 días de anticipación,</strong> asegurando su preparación y alineación con los estándares institucionales.</p>
          </div>
          <div className="mb-3">
            <h5>Fórmula:</h5>
            <img className="img-fluid" src="/images/implementacion_formula.png" alt="Formula for KPI" />
          </div>
          <div className="mb-3">
            <h5>Meta:</h5>
            <ul>
              <li><strong>Contratación:</strong> 100% de las clases con docentes confirmados al menos 7 días antes de su dictado.</li>
              <li><strong>Sensibilización:</strong> 100% de los docentes sensibilizados en la metodología ANDRO® al menos 5 días antes de sus clases.</li>
            </ul>
          </div>
          <div className="mb-3">
            <h5>Plan de Acción:</h5>
            <ol className="mb-3">
              <li><strong>Revisión del Calendario:</strong>
                <ul><li>Identificar todas las sesiones activas al inicio de cada mes y priorizar las contrataciones y sensibilizaciones en función de sus fechas de dictado.</li></ul>
              </li>
              <li><strong>Gestión Activa de Contrataciones:</strong>
                <ul><li>Contactar y confirmar la disponibilidad de los docentes con anticipación. Remitir las invitaciones con al menos 15 días de anticipación para garantizar la cobertura de todas las sesiones.</li></ul>
              </li>
              <li><strong>Programación de Sensibilizaciones:</strong>
                <ul><li>Coordinar sesiones de inducción en la metodología ANDRO® para los docentes, alineándolos con los objetivos andragógicos y los estándares de calidad.</li></ul>
              </li>
              <li><strong>Monitoreo Diario:</strong>
                <ul><li>A través de la Dayli (ceremonia de Scrum que consiste en que cada miembro indique que hizo ayer, que hará hoy y qué impedimentos tiene) para detectar posibles retrasos en la contratación o sensibilización de docentes.</li></ul>
              </li>
              <li><strong>Reporte Mensual:</strong>
                <ul><li>Analizar los resultados mediante informes que comparen el avance real con las metas establecidas, identificando oportunidades de mejora en el proceso.</li></ul>
              </li>
            </ol>
          </div>
          <p>Este KPI permite al curador estructurar sus tareas, optimizando la gestión de tiempos y recursos. Al garantizar contrataciones y sensibilizaciones anticipadas, se minimizan imprevistos que puedan comprometer la calidad de las sesiones. De este modo, se fomenta una experiencia académica integral, asegurando el éxito de los programas educativos y fortaleciendo la relación con los docentes.</p>
        </div>
      ),
    },
    organizacion: {
      titulo: 'Organización de Recursos',
      contenido: (
        <div className="kpi-description">
          <div className="mb-3">
            <p>Este KPI mide la <strong>disponibilidad del 100% de los recursos</strong> para cada sesión, asegurando que estén subidos y listos 3 días antes del inicio de la clase. Los recursos incluyen:</p>
            <ul className="mt-2">
              <li><strong>Sesión online</strong> (enlace de Zoom)</li>
              <li><strong>Sesión offline</strong> (enlace de la grabación de la clase)</li>
              <li><strong>Podcast:</strong> Audio de 5 minutos con introducción al tema por el profesor</li>
              <li><strong>Materiales:</strong> PPT y lecturas principales, secundarias, leyes, normas, etc.</li>
              <li><strong>Actividad:</strong> Cuaderno de trabajo con ejercicios prácticos</li>
              <li><strong>Quiz:</strong> Evaluación con 5 preguntas para medir el aprendizaje</li>
            </ul>
          </div>
          <div className="mb-3">
            <h5>Propósito:</h5>
            <p>Garantizar la <strong>calidad y preparación oportuna</strong> de los recursos para cada sesión, mejorando la experiencia de aprendizaje y facilitando el trabajo del docente.</p>
          </div>
          <div className="mb-3">
            <h5>Fórmula:</h5>
            <img className="img-fluid" src="/images/organizacion_formula.png" alt="Formula for KPI" />
          </div>
          <div className="mb-3">
            <h5>Meta:</h5>
            <p>100% de las sesiones con recursos completos.</p>
          </div>
          <div className="mb-3">
            <h5>Plan de Acción:</h5>
            <ul className="mt-2">
              <li><strong>Control del Calendario:</strong> Verificar semanalmente las sesiones programadas para asegurar que los recursos se suban con anticipación.</li>
              <li><strong>Coordinación Docente:</strong> Alinear con los profesores la entrega de materiales y grabaciones.</li>
              <li><strong>Reporte Mensual:</strong> Generar informes de cumplimiento para detectar y corregir áreas críticas.</li>
            </ul>
          </div>
          <p>Este KPI garantiza que cada sesión cuente con los recursos necesarios, asegurando la <strong>calidad y continuidad</strong> del proceso educativo.</p>
        </div>
      ),
    },
    calidad: {
      titulo: 'Control de Calidad Docente',
      contenido: (
        <div className="kpi-description">
          <div className="mb-3">
            <p>Este KPI mide la <strong>calidad de la enseñanza</strong> a través del nivel de satisfacción de los alumnos, con la meta de alcanzar un <strong>90% o más de satisfacción</strong> por sesión. Al finalizar cada clase, los alumnos completan una encuesta donde califican a los profesores según su adaptación a la <strong>metodología andragógica</strong> de INEDI Posgrados, enfocada en el aprendizaje para adultos.</p>
          </div>
          <div className="mb-3">
            <h5>Propósito:</h5>
            <p>Asegurar que los profesores brinden clases alineadas con la metodología institucional, ofreciendo experiencias de aprendizaje significativas para adultos. El área de curaduría juega un rol clave sensibilizando y capacitando a los docentes para que logren esta adaptación.</p>
          </div>
          <div className="mb-3">
            <h5>Fórmula:</h5>
            <img className="img-fluid" src="/images/control_formula.png" alt="Formula for KPI" />
          </div>
          <div className="mb-3">
            <h5>Meta:</h5>
            <p>90% o más de satisfacción por sesión.</p>
          </div>
          <div className="mb-3">
            <h5>Plan de Acción:</h5>
            <ul className="mt-2">
              <li><strong>Encuestas al Final de Cada Sesión:</strong> Monitorear el nivel de satisfacción docente y recopilar retroalimentación directa de los alumnos.</li>
              <li><strong>Capacitación Continua:</strong> El área de curaduría sensibiliza y capacita a los profesores en la metodología andragógica para asegurar su adaptación a las necesidades de los alumnos adultos.</li>
              <li><strong>Mejora Continua:</strong> Identificar y trabajar con los profesores cuyas calificaciones estén por debajo del 90%, proporcionando coaching personalizado para elevar su desempeño.</li>
            </ul>
          </div>
          <p>Este KPI asegura una enseñanza alineada con los principios institucionales, mejorando la experiencia de los alumnos y motivando a los profesores a ofrecer clases de alta calidad.</p>
        </div>
      ),
    },
  };

  // Estado modal "Ver más" — no necesita Redux, es UI local
  const [verMas, setVerMas] = useState({ tipo: null, datos: [], cargando: false });

  const anios = ['2026', '2025', '2024'];
  const meses = [
    { v: '01', l: 'Enero' },   { v: '02', l: 'Febrero' },   { v: '03', l: 'Marzo' },
    { v: '04', l: 'Abril' },   { v: '05', l: 'Mayo' },      { v: '06', l: 'Junio' },
    { v: '07', l: 'Julio' },   { v: '08', l: 'Agosto' },    { v: '09', l: 'Septiembre' },
    { v: '10', l: 'Octubre' }, { v: '11', l: 'Noviembre' }, { v: '12', l: 'Diciembre' },
  ];

  useEffect(() => {
    obtenerKpisAcademicos(anio, mes);
    obtenerProductosKpi(anio, mes);
    obtenerDocentesKpi(anio, mes);
  }, []);

  const consultar = () => {
    obtenerKpisAcademicos(anio, mes, idProducto || null, idDocente || null);
    obtenerProductosKpi(anio, mes);
    obtenerDocentesKpi(anio, mes, idProducto || null);
  };

  // Abrir modal de detalle (CA13 / CA16)
  const abrirDetalle = async (tipo) => {
    setVerMas({ tipo, datos: [], cargando: true });
    try {
      const endpoint = tipo === 'recursos'
        ? '/admin/kpis-academicos/detalle-recursos'
        : tipo === 'calidad'
        ? '/admin/kpis-academicos/detalle-calidad'
        : '/admin/kpis-academicos/detalle-implementacion';
      const params = { anio, mes };
      if (idProducto) params.id_proyecto = idProducto;
      if (idDocente)  params.id_docente  = idDocente;
      const res = await authAxios.get(endpoint, { params });
      setVerMas({ tipo, datos: res.data, cargando: false });
    } catch (e) {
      setVerMas({ tipo: null, datos: [], cargando: false });
      avisoError('Error al cargar el detalle');
    }
  };

  const sinDatos      = !datos || datos.sin_datos || datos.sesiones_programadas === 0;
  const recursos      = datos?.recursos || [];
  const calProds      = datos?.calidad_productos || [];
  const calDocentes   = datos?.calidad_docentes  || [];

  const recursosFiltrados = filtroRec
    ? recursos.filter(r => r.nombre === filtroRec)
    : recursos;

  const estadoBloque = (pct) => {
    if (sinDatos || pct === null) return 'sin_datos';
    if (pct >= 90) return 'verde';
    if (pct >= 70) return 'amarillo';
    return 'rojo';
  };

  const pctImpl = sinDatos ? null : Math.round((datos.pct_confirmadas + datos.pct_confirmadas_7dias + datos.pct_capacitados) / 3);
  // pct_recursos_3dias del backend usa dt_proceso_sesion (incorrecto); calculamos desde recursos reales
  const pctRec  = sinDatos || recursos.length === 0 ? null
    : Math.round(recursos.reduce((sum, r) => sum + (r.total > 0 ? (r.en_fecha / r.total) * 100 : 0), 0) / recursos.length);
  const pctCal  = sinDatos || !datos?.pct_calidad_docente ? null : datos.pct_calidad_docente;

  const bloqueVisible = (pct) => !filtroKpi || estadoBloque(pct) === filtroKpi;

  // CA17 — alertas automáticas
  const alertas = [];
  if (!sinDatos) {
    if (datos.pct_confirmadas < 90)
      alertas.push(`${datos.sesiones_programadas - datos.sesiones_confirmadas} sesión(es) sin confirmar`);
    if (datos.total_docentes > 0 && datos.pct_capacitados < 90)
      alertas.push(`${datos.total_docentes - datos.docentes_capacitados} docente(s) sin capacitar`);
    if (pctRec !== null && pctRec < 85)
      alertas.push('Recursos incompletos o no cargados a tiempo');
    if (recursos.some(r => r.nombre === 'La clase Offline' && r.cumplidos < r.total))
      alertas.push('Sesiones con clase offline pendiente');
  }

  const periodo = `${meses.find(m => m.v === mes)?.l || mes} ${anio}`;

  // ── CA18: Exportar Excel (async con detalle) ─────────────────
  const exportarExcel = async () => {
    const estado = (pct, meta) => pct === null ? 'Sin datos' : pct >= meta ? 'Cumple' : pct >= meta * 0.78 ? 'En riesgo' : 'No cumple';
    const confirmadas_7dias = Math.round((datos.pct_confirmadas_7dias / 100) * datos.sesiones_programadas);
    const recursos_promedio = pctRec ?? 0;

    const params = { anio, mes };
    if (idProducto) params.id_proyecto = idProducto;
    if (idDocente)  params.id_docente  = idDocente;

    let detImpl = [], detRec = [], detCal = [];
    try {
      const [r1, r2, r3] = await Promise.all([
        authAxios.get('/admin/kpis-academicos/detalle-implementacion', { params }),
        authAxios.get('/admin/kpis-academicos/detalle-recursos',       { params }),
        authAxios.get('/admin/kpis-academicos/detalle-calidad',        { params }),
      ]);
      detImpl = r1.data;
      detRec  = r2.data;
      detCal  = r3.data;
    } catch (e) { /* exportar igual con lo que hay */ }

    const wb = XLSX.utils.book_new();

    const ws1 = XLSX.utils.aoa_to_sheet([
      [`KPI's Académicos — ${periodo}`],
      [],
      ['KPI', 'Cumplidos', 'Total', '%', 'Meta', 'Estado'],
      ['Sesiones programadas',       datos.sesiones_programadas, '—', '—', '—', '—'],
      ['Sesiones confirmadas',       datos.sesiones_confirmadas, datos.sesiones_programadas, datos.pct_confirmadas + '%',        '90%', estado(datos.pct_confirmadas, 90)],
      ['Confirmación con 7 días',    confirmadas_7dias,          datos.sesiones_programadas, datos.pct_confirmadas_7dias + '%',  '80%', estado(datos.pct_confirmadas_7dias, 80)],
      ['Docentes capacitados',       datos.docentes_capacitados, datos.total_docentes,       datos.pct_capacitados + '%',        '90%', estado(datos.pct_capacitados, 90)],
      ['Capacitación con 5 días',    datos.docentes_cap_5dias || 0, datos.total_docentes,    (datos.pct_cap_5dias || 0) + '%',   '80%', estado(datos.pct_cap_5dias || 0, 80)],
      ['Recursos a tiempo (promedio)', recursos_promedio + '%',   '—',                        recursos_promedio + '%',           '85%', estado(recursos_promedio, 85)],
      ['Calidad docente',            '—',                        '—',                         (datos.pct_calidad_docente || 0) + '%', '80%', estado(datos.pct_calidad_docente || 0, 80)],
    ]);
    XLSX.utils.book_append_sheet(wb, ws1, 'KPIs Resumen');

    const ws2 = XLSX.utils.aoa_to_sheet([
      ['Tipo de recurso', 'Cumplidos', 'Total', '%'],
      ...recursos.map(r => [r.nombre, r.cumplidos, r.total, r.total > 0 ? Math.round((r.cumplidos / r.total) * 100) + '%' : '—']),
    ]);
    XLSX.utils.book_append_sheet(wb, ws2, 'Recursos');

    const ws3 = XLSX.utils.aoa_to_sheet([
      ['Producto', 'Promedio', 'Encuestas', 'Meta', 'Estado'],
      ...calProds.map(c => [c.nombre, c.promedio + '%', c.total_encuestas, c.meta + '%',
        c.estado === 'verde' ? 'Cumple' : c.estado === 'amarillo' ? 'En riesgo' : 'No cumple']),
    ]);
    XLSX.utils.book_append_sheet(wb, ws3, 'Calidad Docente');

    if (detImpl.length > 0) {
      const ws4 = XLSX.utils.aoa_to_sheet([
        ['Producto', 'Sesión', 'Fecha', 'Confirmado', 'Fecha confirm.', 'Oportuna (≥7d)', 'Docente responsable', 'Observaciones'],
        ...detImpl.map(r => {
          const obs = !r.fecha_confirmacion ? 'Sin confirmación' : r.confirmacion_oportuna == 0 ? 'Confirmación tardía' : '—';
          return [r.nombre_proyecto, r.nombre_sesion, r.fecha_sesion,
            r.confirmado == 1 ? 'Sí' : 'No', r.fecha_confirmacion || '—',
            r.confirmacion_oportuna == 1 ? 'Oportuna' : r.fecha_confirmacion ? 'Tardía' : '—',
            r.docente_confirmado || 'Sin asignar', obs];
        }),
      ]);
      XLSX.utils.book_append_sheet(wb, ws4, 'Detalle Implementación');
    }

    if (detRec.length > 0) {
      const tiposRec = [...new Set(detRec.flatMap(s => Object.keys(s.recursos || {})))];
      const ws5 = XLSX.utils.aoa_to_sheet([
        ['Producto', 'Sesión', 'Fecha', 'Responsable', ...tiposRec, 'Estado', 'Observaciones'],
        ...detRec.map(r => {
          const obs = r.estado_general === 'Sin recursos' ? 'Sin recursos registrados'
            : r.estado_general === 'Incompleto' ? 'Recursos incompletos' : '—';
          return [r.nombre_proyecto, r.nombre_sesion, r.fecha_sesion, r.responsable || '—',
            ...tiposRec.map(t => r.recursos?.[t] || '—'), r.estado_general, obs];
        }),
      ]);
      XLSX.utils.book_append_sheet(wb, ws5, 'Detalle Recursos');
    }

    if (detCal.length > 0) {
      const ws6 = XLSX.utils.aoa_to_sheet([
        ['Producto', 'Sesión', 'Fecha', 'Docente', 'Calificación', 'Estado', 'Observaciones'],
        ...detCal.map(r => {
          const cal = parseFloat(r.calificacion) || 0;
          const est = cal >= 80 ? 'Cumple' : cal >= 64 ? 'En riesgo' : 'No cumple';
          const obs = cal < 64 ? 'Bajo desempeño' : cal < 80 ? 'Necesita mejorar' : '—';
          return [r.nombre_proyecto, r.nombre_sesion, r.fecha_sesion, r.docente || '—', cal + '%', est, obs];
        }),
      ]);
      XLSX.utils.book_append_sheet(wb, ws6, 'Detalle Calidad');
    }

    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `KPIs_Academicos_${periodo.replace(' ', '_')}.xlsx`);
  };

  // ── CA18: Exportar PDF (async con detalle) ───────────────────
  const exportarPdf = async () => {
    const estado = (pct, meta) => pct === null ? 'Sin datos' : pct >= meta ? 'Cumple' : pct >= meta * 0.78 ? 'En riesgo' : 'No cumple';
    const confirmadas_7dias = Math.round((datos.pct_confirmadas_7dias / 100) * datos.sesiones_programadas);
    const recursos_promedio = pctRec ?? 0;

    const params = { anio, mes };
    if (idProducto) params.id_proyecto = idProducto;
    if (idDocente)  params.id_docente  = idDocente;

    let detImpl = [], detRec = [], detCal = [];
    try {
      const [r1, r2, r3] = await Promise.all([
        authAxios.get('/admin/kpis-academicos/detalle-implementacion', { params }),
        authAxios.get('/admin/kpis-academicos/detalle-recursos',       { params }),
        authAxios.get('/admin/kpis-academicos/detalle-calidad',        { params }),
      ]);
      detImpl = r1.data;
      detRec  = r2.data;
      detCal  = r3.data;
    } catch (e) { /* continuar con lo disponible */ }

    const doc = new jsPDF('p', 'mm', 'a4');

    doc.setFontSize(14);
    doc.setFontStyle('bold');
    doc.text("KPI's Académicos — INEDI Posgrados", 14, 18);
    doc.setFontSize(9);
    doc.setFontStyle('normal');
    doc.text(`Periodo: ${periodo}   |   Generado: ${new Date().toLocaleDateString('es-PE')}`, 14, 25);

    doc.autoTable({
      startY: 30,
      head: [['KPI', 'Cumplidos', 'Total', '%', 'Meta', 'Estado']],
      body: [
        ['Sesiones programadas',      datos.sesiones_programadas, '—', '—', '—', '—'],
        ['Sesiones confirmadas',       datos.sesiones_confirmadas, datos.sesiones_programadas, datos.pct_confirmadas + '%',        '90%', estado(datos.pct_confirmadas, 90)],
        ['Confirmación con 7 días',    confirmadas_7dias,          datos.sesiones_programadas, datos.pct_confirmadas_7dias + '%',  '80%', estado(datos.pct_confirmadas_7dias, 80)],
        ['Docentes capacitados',       datos.docentes_capacitados, datos.total_docentes,       datos.pct_capacitados + '%',        '90%', estado(datos.pct_capacitados, 90)],
        ['Capacitación con 5 días',    datos.docentes_cap_5dias || 0, datos.total_docentes,    (datos.pct_cap_5dias || 0) + '%',  '80%', estado(datos.pct_cap_5dias || 0, 80)],
        ['Recursos a tiempo (promedio)', recursos_promedio + '%',   '—',                        recursos_promedio + '%',           '85%', estado(recursos_promedio, 85)],
        ['Calidad docente',            '—',                        '—', (datos.pct_calidad_docente || 0) + '%', '80%', estado(datos.pct_calidad_docente || 0, 80)],
      ],
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [52, 58, 64], textColor: 255, fontStyle: 'bold' },
      theme: 'striped',
    });

    if (recursos.length > 0) {
      const y1 = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(11);
      doc.setFontStyle('bold');
      doc.text('Recursos por Tipo', 14, y1);
      doc.autoTable({
        startY: y1 + 4,
        head: [['Tipo de recurso', 'Cumplidos', 'Total', '%']],
        body: recursos.map(r => [r.nombre, r.cumplidos, r.total, r.total > 0 ? Math.round((r.cumplidos / r.total) * 100) + '%' : '—']),
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [40, 167, 69], textColor: 255 },
        theme: 'striped',
      });
    }

    if (calProds.length > 0) {
      const y2 = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(11);
      doc.setFontStyle('bold');
      doc.text('Calidad Docente por Producto', 14, y2);
      doc.autoTable({
        startY: y2 + 4,
        head: [['Producto', 'Promedio', 'Encuestas', 'Meta', 'Estado']],
        body: calProds.map(c => [c.nombre, c.promedio + '%', c.total_encuestas, c.meta + '%',
          c.estado === 'verde' ? 'Cumple' : c.estado === 'amarillo' ? 'En riesgo' : 'No cumple']),
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [253, 126, 20], textColor: 255 },
        theme: 'striped',
      });
    }

    if (detImpl.length > 0) {
      doc.addPage();
      doc.setFontSize(12);
      doc.setFontStyle('bold');
      doc.text('Detalle — Implementación Académica', 14, 16);
      doc.autoTable({
        startY: 20,
        head: [['Producto', 'Sesión', 'Fecha', 'Confirmado', 'Fecha conf.', 'Oportuna', 'Docente', 'Observaciones']],
        body: detImpl.map(r => {
          const obs = !r.fecha_confirmacion ? 'Sin confirmación' : r.confirmacion_oportuna == 0 ? 'Conf. tardía' : '—';
          return [r.nombre_proyecto, r.nombre_sesion, r.fecha_sesion,
            r.confirmado == 1 ? 'Sí' : 'No', r.fecha_confirmacion || '—',
            r.confirmacion_oportuna == 1 ? 'Oportuna' : r.fecha_confirmacion ? 'Tardía' : '—',
            r.docente_confirmado || '—', obs];
        }),
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [52, 58, 64], textColor: 255, fontStyle: 'bold', fontSize: 7 },
        theme: 'striped',
      });
    }

    if (detRec.length > 0) {
      doc.addPage();
      doc.setFontSize(12);
      doc.setFontStyle('bold');
      doc.text('Detalle — Organización de Recursos', 14, 16);
      const tiposRec = [...new Set(detRec.flatMap(s => Object.keys(s.recursos || {})))];
      doc.autoTable({
        startY: 20,
        head: [['Producto', 'Sesión', 'Fecha', 'Responsable', ...tiposRec.map(t => t.replace('La clase ', '').replace('Los ', '').replace('La ', '')), 'Estado', 'Observaciones']],
        body: detRec.map(r => {
          const obs = r.estado_general === 'Sin recursos' ? 'Sin recursos' : r.estado_general === 'Incompleto' ? 'Incompleto' : '—';
          return [r.nombre_proyecto, r.nombre_sesion, r.fecha_sesion, r.responsable || '—',
            ...tiposRec.map(t => r.recursos?.[t] || '—'), r.estado_general, obs];
        }),
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [40, 167, 69], textColor: 255, fontStyle: 'bold', fontSize: 7 },
        theme: 'striped',
      });
    }

    if (detCal.length > 0) {
      doc.addPage();
      doc.setFontSize(12);
      doc.setFontStyle('bold');
      doc.text('Detalle — Calidad Docente', 14, 16);
      doc.autoTable({
        startY: 20,
        head: [['Producto', 'Sesión', 'Fecha', 'Docente', 'Calificación', 'Estado', 'Observaciones']],
        body: detCal.map(r => {
          const cal = parseFloat(r.calificacion) || 0;
          const est = cal >= 80 ? 'Cumple' : cal >= 64 ? 'En riesgo' : 'No cumple';
          const obs = cal < 64 ? 'Bajo desempeño' : cal < 80 ? 'Necesita mejorar' : '—';
          return [r.nombre_proyecto, r.nombre_sesion, r.fecha_sesion, r.docente || '—', cal + '%', est, obs];
        }),
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [253, 126, 20], textColor: 255, fontStyle: 'bold', fontSize: 7 },
        theme: 'striped',
      });
    }

    doc.save(`KPIs_Academicos_${periodo.replace(' ', '_')}.pdf`);
  };

  // Columnas dinámicas para modal recursos (CA13)
  const tiposRecurso = verMas.datos.length > 0 && verMas.tipo === 'recursos'
    ? [...new Set(verMas.datos.flatMap(s => Object.keys(s.recursos || {})))]
    : [];

  return (
    <Layout>
      <HeaderContent title="Reporte KPI's Académicos" />
      <Breadcrumb
        link={[
          { url: '/inicio', nombre: 'Inicio', icon: 'fas fa-home' },
          { url: '',        nombre: "Reporte KPI's Académicos" },
        ]}
      />

      {/* ── Filtros ──────────────────────────────────────── */}
      <div className="box p-4 mb-3">
        <div className="row align-items-end">
          <div className="col-md-2 mb-2">
            <label style={{ fontSize: 14, fontWeight: 600, marginBottom: 5 }}>
              <FontAwesomeIcon icon={faCalendarAlt} className="mr-1 text-muted" />Año
            </label>
            <select className="form-control" value={anio} onChange={e => setAnio(e.target.value)}>
              {anios.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="col-md-2 mb-2">
            <label style={{ fontSize: 14, fontWeight: 600, marginBottom: 5 }}>
              <FontAwesomeIcon icon={faCalendarAlt} className="mr-1 text-muted" />Mes
            </label>
            <select className="form-control" value={mes} onChange={e => setMes(e.target.value)}>
              {meses.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
          </div>
          <div className="col-md-2 mb-2">
            <label style={{ fontSize: 14, fontWeight: 600, marginBottom: 5 }}>Producto</label>
            <select className="form-control" value={idProducto} onChange={e => setIdProducto(e.target.value)}>
              <option value="">Todos los productos</option>
              {productos.map(p => (
                <option key={p.id_proyecto} value={p.id_proyecto}>{p.nombre_proyecto}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2 mb-2">
            <label style={{ fontSize: 14, fontWeight: 600, marginBottom: 5 }}>Docente</label>
            <select className="form-control" value={idDocente} onChange={e => setIdDocente(e.target.value)}>
              <option value="">Todos los docentes</option>
              {docentes.map(d => (
                <option key={d.id_docente} value={d.id_docente}>{d.nombre_docente}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2 mb-2">
            <label style={{ fontSize: 14, fontWeight: 600, marginBottom: 5 }}>Estado KPI</label>
            <select className="form-control" value={filtroKpi} onChange={e => setFiltroKpi(e.target.value)}>
              <option value="">Todos</option>
              <option value="verde">Cumple</option>
              <option value="amarillo">En riesgo</option>
              <option value="rojo">No cumple</option>
              <option value="sin_datos">Sin datos</option>
            </select>
          </div>
          <div className="col-md-1 mb-2">
            <label style={{ fontSize: 14, fontWeight: 600, marginBottom: 5 }}>Recurso</label>
            <select className="form-control" value={filtroRec} onChange={e => setFiltroRec(e.target.value)}>
              <option value="">Todos</option>
              {recursos.map((r, i) => <option key={i} value={r.nombre}>{r.nombre}</option>)}
            </select>
          </div>
          <div className="col-md-1 mb-2" style={{ paddingTop: 22 }}>
            <Button variant="primary" block onClick={consultar} disabled={loading}>
              <FontAwesomeIcon icon={faFilter} />
            </Button>
          </div>
        </div>
      </div>

      {/* ── CA18: Botones de exportación ─────────────────── */}
      {!sinDatos && (
        <div className="d-flex justify-content-end mb-3">
          <Button variant="outline-success" size="sm" className="mr-2" onClick={exportarExcel} disabled={loading}>
            <FontAwesomeIcon icon={faFileExcel} className="mr-1" />Excel
          </Button>
          <Button variant="outline-danger" size="sm" onClick={exportarPdf} disabled={loading}>
            <FontAwesomeIcon icon={faFilePdf} className="mr-1" />PDF
          </Button>
        </div>
      )}

      <SpinnerLoading loading={loading}>

        {/* ── Alertas CA17 ─────────────────────────────────── */}
        {alertas.length > 0 && (
          <div className="mb-3">
            {alertas.map((a, i) => (
              <div key={i} className="d-flex align-items-center p-2 mb-1"
                style={{ backgroundColor: '#fff3cd', borderRadius: 6, fontSize: 14 }}>
                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 text-warning" />
                {a}
              </div>
            ))}
          </div>
        )}

        {/* ── Tarjetas resumen por bloque ──────────────────── */}
        <div className="row mb-1">

          {/* Sesiones del mes */}
          <div className="col-xl-3 col-md-6 mb-3">
            <div className="box p-3 h-100" style={{ borderTop: '4px solid #17a2b8' }}>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div style={{ width: 38, height: 38, borderRadius: 8, backgroundColor: '#17a2b820', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#17a2b8', fontSize: 17 }} />
                </div>
                <span style={{ fontSize: 13, color: '#17a2b8', fontWeight: 600, backgroundColor: '#17a2b815', padding: '2px 10px', borderRadius: 20 }}>{periodo}</span>
              </div>
              <h2 style={{ color: '#17a2b8', fontWeight: 700, fontSize: 36, marginBottom: 2 }}>{sinDatos ? '—' : datos.sesiones_programadas}</h2>
              <p style={{ fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 10 }}>Sesiones programadas</p>
              {!sinDatos && (
                <>
                  <div style={{ height: 6, backgroundColor: '#e9ecef', borderRadius: 3, marginBottom: 8 }}>
                    <div style={{ height: 6, width: `${datos.pct_confirmadas}%`, backgroundColor: '#17a2b8', borderRadius: 3, transition: 'width .4s' }} />
                  </div>
                  <div className="d-flex justify-content-between" style={{ fontSize: 13 }}>
                    <span style={{ color: '#28a745', fontWeight: 600 }}>✓ {datos.sesiones_confirmadas} confirmadas</span>
                    <span style={{ color: '#6c757d' }}>{datos.sesiones_programadas - datos.sesiones_confirmadas} pendientes</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Implementación */}
          {(() => {
            const color = colorSemaforo(sinDatos ? null : pctImpl, sinDatos);
            const label = labelSemaforo(sinDatos ? null : pctImpl, sinDatos);
            const metricas = sinDatos ? [] : [
              { label: 'Confirmadas',  pct: datos.pct_confirmadas,      meta: 90 },
              { label: 'Anticip. ≥7d', pct: datos.pct_confirmadas_7dias, meta: 80 },
              { label: 'Docentes',     pct: datos.pct_capacitados,       meta: 90 },
            ];
            return (
              <div className="col-xl-3 col-md-6 mb-3">
                <div className="box p-3 h-100" style={{ borderTop: `4px solid ${color}` }}>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div style={{ width: 38, height: 38, borderRadius: 8, backgroundColor: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FontAwesomeIcon icon={faChartLine} style={{ color, fontSize: 17 }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color, backgroundColor: `${color}15`, padding: '2px 10px', borderRadius: 20 }}>{label}</span>
                  </div>
                  <h2 style={{ color, fontWeight: 700, fontSize: 36, marginBottom: 2 }}>{sinDatos ? '—' : `${pctImpl}%`}</h2>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 10 }}>Implementación Académica</p>
                  <div style={{ height: 6, backgroundColor: '#e9ecef', borderRadius: 3, marginBottom: 10 }}>
                    <div style={{ height: 6, width: `${Math.min(sinDatos ? 0 : pctImpl, 100)}%`, backgroundColor: color, borderRadius: 3, transition: 'width .4s' }} />
                  </div>
                  {metricas.map((m, i) => {
                    const mc = colorSemaforo(m.pct);
                    return (
                      <div key={i} className="d-flex align-items-center justify-content-between py-1" style={{ borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>
                        <div className="d-flex align-items-center">
                          <div style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: mc, marginRight: 7, flexShrink: 0 }} />
                          <span style={{ color: '#555' }}>{m.label}</span>
                        </div>
                        <span style={{ fontWeight: 700, color: mc }}>{m.pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Organización */}
          {(() => {
            const color = colorSemaforo(sinDatos ? null : pctRec, sinDatos);
            const label = labelSemaforo(sinDatos ? null : pctRec, sinDatos);
            return (
              <div className="col-xl-3 col-md-6 mb-3">
                <div className="box p-3 h-100" style={{ borderTop: `4px solid ${color}` }}>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div style={{ width: 38, height: 38, borderRadius: 8, backgroundColor: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FontAwesomeIcon icon={faBook} style={{ color, fontSize: 17 }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color, backgroundColor: `${color}15`, padding: '2px 10px', borderRadius: 20 }}>{label}</span>
                  </div>
                  <h2 style={{ color, fontWeight: 700, fontSize: 36, marginBottom: 2 }}>{sinDatos ? '—' : `${pctRec}%`}</h2>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 10 }}>Organización de Recursos</p>
                  <div style={{ height: 6, backgroundColor: '#e9ecef', borderRadius: 3, marginBottom: 10 }}>
                    <div style={{ height: 6, width: `${Math.min(sinDatos ? 0 : pctRec, 100)}%`, backgroundColor: color, borderRadius: 3, transition: 'width .4s' }} />
                  </div>
                  {recursos.slice(0, 3).map((r, i) => {
                    const pct = r.total > 0 ? Math.round((r.cumplidos / r.total) * 100) : null;
                    const rc = colorSemaforo(pct, pct === null);
                    return (
                      <div key={i} className="d-flex align-items-center justify-content-between py-1" style={{ borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>
                        <div className="d-flex align-items-center">
                          <div style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: rc, marginRight: 7, flexShrink: 0 }} />
                          <span style={{ color: '#555' }}>{r.nombre.replace('La clase ', '').replace('Los ', '').replace('La ', '')}</span>
                        </div>
                        <span style={{ fontWeight: 700, color: rc }}>{pct !== null ? `${pct}%` : '—'}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Calidad */}
          {(() => {
            const sinCal = sinDatos || !datos?.pct_calidad_docente;
            const color  = colorSemaforo(sinCal ? null : pctCal, sinCal);
            const label  = labelSemaforo(sinCal ? null : pctCal, sinCal);
            return (
              <div className="col-xl-3 col-md-6 mb-3">
                <div className="box p-3 h-100" style={{ borderTop: `4px solid ${color}` }}>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div style={{ width: 38, height: 38, borderRadius: 8, backgroundColor: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FontAwesomeIcon icon={faStar} style={{ color, fontSize: 17 }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color, backgroundColor: `${color}15`, padding: '2px 10px', borderRadius: 20 }}>{label}</span>
                  </div>
                  <h2 style={{ color, fontWeight: 700, fontSize: 36, marginBottom: 2 }}>{sinCal ? '—' : `${pctCal}%`}</h2>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 10 }}>Control de Calidad Docente</p>
                  <div style={{ height: 6, backgroundColor: '#e9ecef', borderRadius: 3, marginBottom: 10 }}>
                    <div style={{ height: 6, width: `${Math.min(sinCal ? 0 : pctCal, 100)}%`, backgroundColor: color, borderRadius: 3, transition: 'width .4s' }} />
                  </div>
                  {!sinCal && calProds.slice(0, 3).map((c, i) => {
                    const cc = colorEstado[c.estado] || '#6c757d';
                    return (
                      <div key={i} className="d-flex align-items-center justify-content-between py-1" style={{ borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>
                        <div className="d-flex align-items-center" style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: cc, marginRight: 7, flexShrink: 0 }} />
                          <span style={{ color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nombre}</span>
                        </div>
                        <span style={{ fontWeight: 700, color: cc, marginLeft: 8, flexShrink: 0 }}>{c.promedio}%</span>
                      </div>
                    );
                  })}
                  {sinCal && <p style={{ fontSize: 13, color: '#999', marginBottom: 0 }}>Sin encuestas registradas este mes</p>}
                </div>
              </div>
            );
          })()}

        </div>

        {/* ── Bloque 1: Implementación Académica ───────────── */}
        {bloqueVisible(pctImpl) && (
          <BloqueKpi
            titulo="Implementación Académica"
            icon={faChartLine}
            pct={pctImpl}
            sinDatos={sinDatos}
            onInfo={() => setInfoKpi('implementacion')}
            onVerMas={!sinDatos ? () => abrirDetalle('implementacion') : null}
          >
            {!sinDatos && (() => {
              const totalSes    = datos.sesiones_programadas || 0;
              const confirmadas = datos.sesiones_confirmadas || 0;
              const a_tiempo    = Math.round((datos.pct_confirmadas_7dias / 100) * totalSes);
              const tarde_ses   = Math.max(0, confirmadas - a_tiempo);
              const sin_conf    = Math.max(0, totalSes - confirmadas);

              const totalDoc    = datos.total_docentes || 0;
              const capacitados = datos.docentes_capacitados || 0;
              const a_tiempo_d  = datos.docentes_cap_5dias || 0;
              const tarde_doc   = Math.max(0, capacitados - a_tiempo_d);
              const sin_cap     = Math.max(0, totalDoc - capacitados);

              const Fila = ({ label, n, total, dotColor }) => {
                const pct = total > 0 ? Math.round((n / total) * 100) : 0;
                return (
                  <div className="d-flex align-items-center justify-content-between py-2" style={{ borderBottom: '1px solid #f0f0f0', fontSize: 15 }}>
                    <div className="d-flex align-items-center">
                      <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: dotColor, marginRight: 10, flexShrink: 0 }} />
                      <span style={{ fontWeight: 500, color: '#444' }}>{label}</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontWeight: 700, color: dotColor, fontSize: 22 }}>{n}</span>
                      <span className="text-muted ml-2" style={{ fontSize: 14 }}>({pct}% · {n}/{total})</span>
                    </div>
                  </div>
                );
              };

              const pctConf = totalSes > 0 ? Math.round((confirmadas / totalSes) * 100) : 0;
              const pctCap  = totalDoc > 0 ? Math.round((capacitados / totalDoc) * 100) : 0;
              const cConf   = colorSemaforo(pctConf);
              const cCap    = colorSemaforo(pctCap);

              const ResumenSub = ({ label, n, total, pct, color }) => (
                <div className="d-flex align-items-center justify-content-between mb-2 px-3 py-2"
                  style={{ backgroundColor: `${color}10`, borderRadius: 8, borderLeft: `3px solid ${color}` }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#444' }}>{label}</span>
                  <div className="d-flex align-items-center" style={{ gap: 12 }}>
                    <span style={{ fontSize: 17, color: '#666' }}>{n} / {total}</span>
                    <span style={{ fontSize: 26, fontWeight: 700, color }}>{pct}%</span>
                  </div>
                </div>
              );

              return (
                <>
                  <p style={{ fontSize: 14, fontWeight: 700, color: '#657FC5', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 12, marginBottom: 6 }}>
                    Confirmar / Contratar docentes
                  </p>
                  <ResumenSub label="Sesiones confirmadas" n={confirmadas} total={totalSes} pct={pctConf} color={cConf} />
                  <Fila label="A tiempo (≥7 días)"  n={a_tiempo}  total={totalSes} dotColor="#28a745" />
                  <Fila label="Tarde (&lt;7 días)"   n={tarde_ses} total={totalSes} dotColor="#fd7e14" />
                  <Fila label="Sin confirmar"         n={sin_conf}  total={totalSes} dotColor="#6c757d" />

                  <p style={{ fontSize: 14, fontWeight: 700, color: '#657FC5', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 16, marginBottom: 6 }}>
                    Sensibilizar / Inducir docentes
                  </p>
                  <ResumenSub label="Docentes capacitados" n={capacitados} total={totalDoc} pct={pctCap} color={cCap} />
                  <Fila label="A tiempo (≥5 días)"  n={a_tiempo_d} total={totalDoc} dotColor="#28a745" />
                  <Fila label="Tarde (&lt;5 días)"   n={tarde_doc}  total={totalDoc} dotColor="#fd7e14" />
                  <Fila label="Sin capacitar"         n={sin_cap}    total={totalDoc} dotColor="#6c757d" />
                </>
              );
            })()}
          </BloqueKpi>
        )}

        {/* ── Bloque 2: Organización de Recursos ───────────── */}
        {bloqueVisible(pctRec) && (
          <BloqueKpi
            titulo="Organización de Recursos"
            icon={faBook}
            pct={pctRec}
            sinDatos={sinDatos}
            onInfo={() => setInfoKpi('organizacion')}
            onVerMas={!sinDatos ? () => abrirDetalle('recursos') : null}
          >
            {recursosFiltrados.length === 0 ? (
              <p className="text-muted text-center py-3" style={{ fontSize: 14 }}>Sin registros de recursos para el periodo seleccionado.</p>
            ) : (
              <div className="table-responsive mt-1">
                <table className="table mb-0" style={{ fontSize: 15 }}>
                  <thead>
                    <tr style={{ fontSize: 14, borderBottom: '2px solid #e9ecef' }}>
                      <th style={{ color: '#666', fontWeight: 600, paddingBottom: 8 }}>Recurso</th>
                      <th className="text-center" style={{ color: '#28a745', fontWeight: 700, minWidth: 150 }}>
                        ✓ A tiempo (≥3 días)
                      </th>
                      <th className="text-center" style={{ color: '#dc3545', fontWeight: 700, minWidth: 130 }}>
                        ✗ Tardío
                      </th>
                      <th className="text-center" style={{ color: '#6c757d', fontWeight: 600, minWidth: 110 }}>Subido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recursosFiltrados.map((r, i) => {
                      const pctEnFecha = r.total > 0 ? Math.round((r.en_fecha   / r.total) * 100) : null;
                      const pctTarde   = r.total > 0 ? Math.round((r.tarde      / r.total) * 100) : null;
                      const pctSubido  = r.total > 0 ? Math.round((r.cumplidos  / r.total) * 100) : null;
                      const dotColor   = colorSemaforo(pctEnFecha, pctEnFecha === null);
                      const subColor   = pctSubido >= 80 ? '#28a745' : pctSubido >= 50 ? '#fd7e14' : '#dc3545';
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                          <td className="align-middle" style={{ fontWeight: 500 }}>
                            <div className="d-flex align-items-center">
                              <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: dotColor, marginRight: 9, flexShrink: 0 }} />
                              {r.nombre}
                              {r.nombre === 'La clase Offline' && (
                                <Badge variant="warning" className="ml-2" style={{ fontSize: 11 }}>Oblig.</Badge>
                              )}
                            </div>
                          </td>
                          <td className="text-center align-middle">
                            <span style={{ fontWeight: 700, color: '#28a745', fontSize: 22 }}>{r.en_fecha ?? '—'}</span>
                            {pctEnFecha !== null && (
                              <span style={{ fontSize: 17, fontWeight: 600, color: '#28a745', marginLeft: 6 }}>({pctEnFecha}%)</span>
                            )}
                          </td>
                          <td className="text-center align-middle">
                            <span style={{ fontWeight: 700, color: r.tarde > 0 ? '#dc3545' : '#6c757d', fontSize: 22 }}>{r.tarde ?? '—'}</span>
                            {pctTarde !== null && (
                              <span style={{ fontSize: 17, fontWeight: 600, color: r.tarde > 0 ? '#dc3545' : '#6c757d', marginLeft: 6 }}>({pctTarde}%)</span>
                            )}
                          </td>
                          <td className="text-center align-middle">
                            <span style={{ fontWeight: 700, color: subColor, fontSize: 22 }}>{r.cumplidos}</span>
                            <span style={{ fontSize: 17, color: '#888', marginLeft: 4 }}>/ {r.total}</span>
                            {pctSubido !== null && (
                              <div style={{ fontSize: 16, color: subColor, fontWeight: 700 }}>{pctSubido}%</div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid #e9ecef', backgroundColor: '#f8f9fa' }}>
                      <td style={{ fontWeight: 600, fontSize: 15 }}>Promedio a tiempo (todos los recursos)</td>
                      <td className="text-center" colSpan={2}>
                        <span style={{ fontWeight: 700, color: colorSemaforo(pctRec, pctRec === null), fontSize: 22 }}>
                          {pctRec !== null ? `${pctRec}%` : '—'}
                        </span>
                        <span className="text-muted ml-2" style={{ fontSize: 14 }}>promedio de {recursos.length} recursos</span>
                      </td>
                      <td className="text-center text-muted" style={{ fontSize: 14 }}>
                        {sinDatos ? '—' : `${datos.sesiones_programadas} ses.`}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </BloqueKpi>
        )}

        {/* ── Bloque 3: Control de Calidad Docente CA14 ────── */}
        {bloqueVisible(pctCal) && (
          <BloqueKpi titulo="Control de Calidad Docente" icon={faStar} pct={pctCal} sinDatos={sinDatos || !datos?.pct_calidad_docente}
            onInfo={() => setInfoKpi('calidad')}
            onVerMas={!sinDatos && datos?.pct_calidad_docente ? () => abrirDetalle('calidad') : null}>
            {calProds.length === 0 ? (
              <p className="text-muted text-center py-3" style={{ fontSize: 14 }}>Sin registros de calidad docente para el periodo seleccionado.</p>
            ) : (
              <>
              <div className="table-responsive mb-3">
                <table className="table table-bordered mb-0" style={{ fontSize: 16 }}>
                  <thead className="thead-light">
                    <tr style={{ fontSize: 15, fontWeight: 600 }}>
                      <th>Producto</th>
                      <th className="text-center" style={{ width: 120 }}>Promedio</th>
                      <th className="text-center" style={{ width: 120 }}>Encuestas</th>
                      <th className="text-center" style={{ width: 90 }}>Meta</th>
                      <th className="text-center" style={{ width: 130 }}>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calProds.map((c, i) => {
                      const color = colorEstado[c.estado] || '#6c757d';
                      return (
                        <tr key={i}>
                          <td className="align-middle" style={{ fontWeight: 500 }}>{c.nombre}</td>
                          <td className="text-center align-middle">
                            <span style={{ fontWeight: 700, fontSize: 22, color }}>{c.promedio}%</span>
                          </td>
                          <td className="text-center align-middle">{c.total_encuestas}</td>
                          <td className="text-center align-middle text-muted">{c.meta}%</td>
                          <td className="text-center align-middle">
                            <span style={{ fontSize: 15, fontWeight: 600, color, backgroundColor: `${color}15`, padding: '4px 12px', borderRadius: 20 }}>
                              {c.estado === 'verde' ? 'Cumple' : c.estado === 'amarillo' ? 'En riesgo' : 'No cumple'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {calDocentes.length > 0 && (
                <div className="table-responsive">
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#555', marginBottom: 6 }}>
                    Calificación por docente
                  </div>
                  <table className="table table-bordered mb-0" style={{ fontSize: 15 }}>
                    <thead className="thead-light">
                      <tr style={{ fontSize: 14, fontWeight: 600 }}>
                        <th>#</th>
                        <th>Docente</th>
                        <th className="text-center" style={{ width: 120 }}>Promedio</th>
                        <th className="text-center" style={{ width: 110 }}>Encuestas</th>
                        <th className="text-center" style={{ width: 130 }}>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calDocentes.map((d, i) => {
                        const sinEnc = d.estado === 'sin_encuestas';
                        const color  = sinEnc ? '#6c757d' : (colorEstado[d.estado] || '#6c757d');
                        return (
                          <tr key={i}>
                            <td className="text-center align-middle text-muted" style={{ width: 36 }}>{i + 1}</td>
                            <td className="align-middle" style={{ fontWeight: 500 }}>{d.nombre}</td>
                            <td className="text-center align-middle">
                              {sinEnc
                                ? <span className="text-muted" style={{ fontSize: 14 }}>—</span>
                                : <span style={{ fontWeight: 700, fontSize: 20, color }}>{d.promedio}%</span>
                              }
                            </td>
                            <td className="text-center align-middle text-muted">{d.total_encuestas}</td>
                            <td className="text-center align-middle">
                              <span style={{ fontSize: 14, fontWeight: 600, color, backgroundColor: `${color}15`, padding: '3px 10px', borderRadius: 20 }}>
                                {sinEnc ? 'Sin encuestas' : d.estado === 'verde' ? 'Cumple' : d.estado === 'amarillo' ? 'En riesgo' : 'No cumple'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              </>
            )}
          </BloqueKpi>
        )}

      </SpinnerLoading>

      {/* ── Modal definición KPI ─────────────────────────────── */}
      {(() => {
        const def = infoKpi ? definicionesKpi[infoKpi] : null;
        if (!def) return null;
        return (
          <Modal show={!!infoKpi} onHide={() => setInfoKpi(null)} size="lg">
            <Modal.Header closeButton style={{ borderBottom: '3px solid #17a2b8' }}>
              <Modal.Title style={{ fontSize: 16 }}>
                <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-info" />
                {def.titulo} — Definición del KPI
              </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '20px 24px' }}>
              {def.contenido}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" size="sm" onClick={() => setInfoKpi(null)}>Cerrar</Button>
            </Modal.Footer>
          </Modal>
        );
      })()}

      {/* ── Modal detalle — Implementación Académica (CA16) ── */}
      <Modal show={verMas.tipo === 'implementacion'} onHide={() => setVerMas({ tipo: null, datos: [], cargando: false })} size="xl">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 15 }}>
            <FontAwesomeIcon icon={faChartLine} className="mr-2 text-primary" />
            Detalle — Implementación Académica
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '12px 16px' }}>
          {verMas.cargando ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" size="sm" className="mr-2" />
              <span style={{ fontSize: 14 }}>Cargando detalle...</span>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-bordered table-hover mb-0" style={{ fontSize: 13 }}>
                <thead className="thead-light">
                  <tr>
                    <th className="text-center" style={{ width: 35 }}>#</th>
                    <th>Producto</th>
                    <th>Sesión</th>
                    <th className="text-center" style={{ width: 95 }}>Fecha</th>
                    <th className="text-center" style={{ width: 100 }}>Confirmado</th>
                    <th className="text-center" style={{ width: 110 }}>Fecha confirm.</th>
                    <th className="text-center" style={{ width: 110 }}>Oportuna (≥7d)</th>
                    <th>Docente responsable</th>
                    <th>Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {verMas.datos.length === 0 ? (
                    <tr><td colSpan="9" className="text-center text-muted py-3">Sin registros</td></tr>
                  ) : verMas.datos.map((row, i) => {
                    const obs = !row.fecha_confirmacion ? 'Sin confirmación'
                      : row.confirmacion_oportuna == 0 ? 'Confirmación tardía' : '—';
                    const obsColor = !row.fecha_confirmacion ? '#dc3545'
                      : row.confirmacion_oportuna == 0 ? '#fd7e14' : '#6c757d';
                    return (
                      <tr key={i}>
                        <td className="text-center align-middle">{i + 1}</td>
                        <td className="align-middle" style={{ maxWidth: 180, wordBreak: 'break-word' }}>{row.nombre_proyecto}</td>
                        <td className="align-middle">{row.nombre_sesion}</td>
                        <td className="text-center align-middle">{row.fecha_sesion}</td>
                        <td className="text-center align-middle">
                          {row.confirmado == 1
                            ? <span style={{ color: '#28a745', fontWeight: 600 }}>✓ Sí</span>
                            : <span style={{ color: '#dc3545', fontWeight: 600 }}>✗ No</span>
                          }
                        </td>
                        <td className="text-center align-middle text-muted">{row.fecha_confirmacion || '—'}</td>
                        <td className="text-center align-middle">
                          {row.fecha_confirmacion
                            ? (row.confirmacion_oportuna == 1
                              ? <span style={{ color: '#28a745', fontWeight: 600, fontSize: 13 }}>Oportuna</span>
                              : <span style={{ color: '#fd7e14', fontWeight: 600, fontSize: 13 }}>Tardía</span>)
                            : <span className="text-muted" style={{ fontSize: 13 }}>—</span>
                          }
                        </td>
                        <td className="align-middle" style={{ fontSize: 13 }}>
                          {row.docente_confirmado || <span className="text-muted">Sin asignar</span>}
                        </td>
                        <td className="align-middle">
                          <span style={{ fontSize: 12, fontWeight: 600, color: obsColor }}>{obs}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ padding: '8px 16px' }}>
          <small className="text-muted mr-auto">{verMas.datos.length} sesión(es)</small>
          <Button variant="secondary" size="sm" onClick={() => setVerMas({ tipo: null, datos: [], cargando: false })}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal detalle — Organización de Recursos (CA13 / CA16) ── */}
      <Modal show={verMas.tipo === 'recursos'} onHide={() => setVerMas({ tipo: null, datos: [], cargando: false })} size="xl">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 15 }}>
            <FontAwesomeIcon icon={faBook} className="mr-2 text-success" />
            Detalle — Organización de Recursos
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '12px 16px' }}>
          {verMas.cargando ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="success" size="sm" className="mr-2" />
              <span style={{ fontSize: 14 }}>Cargando detalle...</span>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-bordered table-hover mb-0" style={{ fontSize: 13 }}>
                <thead className="thead-light">
                  <tr>
                    <th className="text-center" style={{ width: 35 }}>#</th>
                    <th>Producto</th>
                    <th>Sesión</th>
                    <th className="text-center" style={{ width: 90 }}>Fecha</th>
                    <th>Responsable</th>
                    {tiposRecurso.map(t => (
                      <th key={t} className="text-center" style={{ minWidth: 90, fontSize: 13 }}>
                        {t.replace('La clase ', '').replace('Los ', '').replace('La ', '')}
                        {t === 'La clase Offline' && <Badge variant="warning" className="ml-1" style={{ fontSize: 11 }}>Oblig.</Badge>}
                      </th>
                    ))}
                    <th className="text-center" style={{ width: 100 }}>Estado</th>
                    <th style={{ minWidth: 120 }}>Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {verMas.datos.length === 0 ? (
                    <tr><td colSpan={6 + tiposRecurso.length} className="text-center text-muted py-3">Sin registros</td></tr>
                  ) : verMas.datos.map((row, i) => {
                    const color = row.estado_general === 'Completo' ? '#28a745'
                      : row.estado_general === 'Incompleto' ? '#dc3545' : '#6c757d';
                    const obs = row.estado_general === 'Sin recursos' ? 'Sin recursos registrados'
                      : row.estado_general === 'Incompleto' ? 'Recursos incompletos' : '—';
                    return (
                      <tr key={i}>
                        <td className="text-center align-middle">{i + 1}</td>
                        <td className="align-middle" style={{ maxWidth: 180, wordBreak: 'break-word' }}>{row.nombre_proyecto}</td>
                        <td className="align-middle">{row.nombre_sesion}</td>
                        <td className="text-center align-middle">{row.fecha_sesion}</td>
                        <td className="align-middle" style={{ fontSize: 13 }}>{row.responsable || <span className="text-muted">—</span>}</td>
                        {tiposRecurso.map(t => (
                          <td key={t} className="text-center align-middle">
                            <BadgeRecurso estado={row.recursos?.[t]} />
                          </td>
                        ))}
                        <td className="text-center align-middle">
                          <span style={{ fontSize: 12, fontWeight: 600, color, backgroundColor: `${color}20`, padding: '2px 6px', borderRadius: 10 }}>
                            {row.estado_general}
                          </span>
                        </td>
                        <td className="align-middle">
                          <span style={{ fontSize: 12, fontWeight: 600, color }}>{obs}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ padding: '8px 16px' }}>
          <small className="text-muted mr-auto">{verMas.datos.length} sesión(es)</small>
          <Button variant="secondary" size="sm" onClick={() => setVerMas({ tipo: null, datos: [], cargando: false })}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal detalle — Calidad Docente (CA16) ── */}
      <Modal show={verMas.tipo === 'calidad'} onHide={() => setVerMas({ tipo: null, datos: [], cargando: false })} size="xl">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 15 }}>
            <FontAwesomeIcon icon={faStar} className="mr-2 text-warning" />
            Detalle — Calidad Docente
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '12px 16px' }}>
          {verMas.cargando ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="warning" size="sm" className="mr-2" />
              <span style={{ fontSize: 14 }}>Cargando detalle...</span>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-bordered table-hover mb-0" style={{ fontSize: 13 }}>
                <thead className="thead-light">
                  <tr>
                    <th className="text-center" style={{ width: 35 }}>#</th>
                    <th>Producto</th>
                    <th>Sesión</th>
                    <th className="text-center" style={{ width: 95 }}>Fecha</th>
                    <th>Docente</th>
                    <th className="text-center" style={{ width: 100 }}>Calificación</th>
                    <th className="text-center" style={{ width: 110 }}>Estado</th>
                    <th style={{ minWidth: 130 }}>Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {verMas.datos.length === 0 ? (
                    <tr><td colSpan="8" className="text-center text-muted py-3">Sin registros</td></tr>
                  ) : verMas.datos.map((row, i) => {
                    const cal   = parseFloat(row.calificacion) || 0;
                    const color = cal >= 80 ? '#28a745' : cal >= 64 ? '#fd7e14' : '#dc3545';
                    const label = cal >= 80 ? 'Cumple' : cal >= 64 ? 'En riesgo' : 'No cumple';
                    const obs   = cal < 64 ? 'Bajo desempeño' : cal < 80 ? 'Necesita mejorar' : '—';
                    return (
                      <tr key={i}>
                        <td className="text-center align-middle">{i + 1}</td>
                        <td className="align-middle" style={{ maxWidth: 180, wordBreak: 'break-word' }}>{row.nombre_proyecto}</td>
                        <td className="align-middle">{row.nombre_sesion}</td>
                        <td className="text-center align-middle">{row.fecha_sesion}</td>
                        <td className="align-middle">{row.docente || <span className="text-muted">—</span>}</td>
                        <td className="text-center align-middle">
                          <span style={{ fontWeight: 700, color }}>{cal}%</span>
                        </td>
                        <td className="text-center align-middle">
                          <span style={{ fontSize: 13, fontWeight: 600, color, backgroundColor: `${color}15`, padding: '2px 8px', borderRadius: 20 }}>
                            {label}
                          </span>
                        </td>
                        <td className="align-middle">
                          <span style={{ fontSize: 12, fontWeight: 600, color }}>{obs}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ padding: '8px 16px' }}>
          <small className="text-muted mr-auto">{verMas.datos.length} sesión(es)</small>
          <Button variant="secondary" size="sm" onClick={() => setVerMas({ tipo: null, datos: [], cargando: false })}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

    </Layout>
  );
};

const mapStateToProps = state => ({
  loading:   state.kpisAcademicos.loading,
  datos:     state.kpisAcademicos.datos,
  productos: state.kpisAcademicos.productos,
  docentes:  state.kpisAcademicos.docentes,
});

const mapDispatchToProps = { obtenerKpisAcademicos, obtenerProductosKpi, obtenerDocentesKpi };

export default connect(mapStateToProps, mapDispatchToProps)(ReporteKpis);
